import React, { useState, useEffect, useCallback } from 'react'
import { Modal, Spin } from 'antd'
import { Document, Page, pdfjs } from 'react-pdf'
import { IoAdd, IoRemove, IoArrowBack } from 'react-icons/io5'
import { BsZoomIn, BsZoomOut } from 'react-icons/bs'
import { AiFillFilePdf } from 'react-icons/ai'
import styled from 'styled-components'
import { useIntersectionObserver } from '@wojtekmaj/react-hooks'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const observerConfig = {
  threshold: 0.3,
}

// Custom hook để lấy kích thước window
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

export default function PreviewFile({ file, isVisible, setIsVisible }) {
  const [numPages, setNumPages] = useState(null)
  const [visiblePages, setVisiblePages] = useState({})
  const [scale, setScale] = useState(1)
  const [fileUrl, setFileUrl] = useState(null)
  const [isPDF, setIsPDF] = useState(false)
  const [isImage, setIsImage] = useState(false)

  const size = useWindowSize()
  const isMobile = size.width <= 739
  const minWidth = isMobile ? size.width : (size.width * 2) / 3
  const maxWidth = isMobile ? size.width : minWidth + 1000

  const [width, setWidth] = useState(minWidth)

  useEffect(() => {
    if (size.width <= 739) {
      setWidth(size.width)
    } else {
      setWidth((size.width * 2) / 3)
    }
  }, [size.width])

  // Xử lý file từ Upload component
  useEffect(() => {
    if (file) {
      // Kiểm tra xem file có phải từ Upload component không
      const fileObj = file.originFileObj || file

      // Xác định loại file
      const fileType = fileObj.type || file.type
      setIsPDF(fileType === 'application/pdf')
      setIsImage(fileType?.startsWith('image/'))

      // Tạo URL cho file
      if (fileObj instanceof File || fileObj instanceof Blob) {
        const url = URL.createObjectURL(fileObj)
        setFileUrl(url)

        // Cleanup URL khi component unmount
        return () => {
          URL.revokeObjectURL(url)
        }
      } else if (file.url || file.thumbUrl) {
        // Nếu file đã có URL (file đã upload)
        setFileUrl(file.url || file.thumbUrl)
      }
    }
  }, [file])

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages)
  }

  const setPageVisibility = useCallback((pageNumber, isIntersecting) => {
    setVisiblePages(prevVisiblePages => {
      const newVisiblePages = {
        ...prevVisiblePages,
        [pageNumber]: isIntersecting,
      }
      const values_true = Object.entries(newVisiblePages).filter(([key, value]) => value)
      if (values_true.length === 0) {
        return {
          ...prevVisiblePages,
        }
      }
      if (isIntersecting) {
        return {
          ...newVisiblePages,
          [pageNumber - 1]: false,
          [pageNumber + 1]: false,
        }
      }
      return newVisiblePages
    })
  }, [])

  const handleZoomOut = () => {
    if (width > minWidth) {
      setWidth(Math.max(width - 200, minWidth))
    }
  }

  const handleZoomIn = () => {
    if (width < maxWidth) {
      setWidth(Math.min(width + 200, maxWidth))
    }
  }

  const currentPage = Object.entries(visiblePages).filter(([key, value]) => value)[0]?.[0] || 1

  return (
    <PreviewStyle width={size.width} height={size.height}>
      {numPages && isPDF && isVisible && (
        <div className='body'>
          <div className='top' onClick={() => setIsVisible(false)}>
            <IoArrowBack style={{ color: '#fff', fontSize: 25 }} />
            <AiFillFilePdf style={{ color: '#EC1C2A', fontSize: 17, marginLeft: 30, marginRight: 12 }} />
            <p>{file?.name || 'Document'}</p>
          </div>
          <div className='bottom'>
            <div className='page-ctn'>
              <p>
                {'Trang   '}
                {currentPage} / {numPages}
              </p>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.5)', width: 1, height: 25, margin: '0px 12px' }} />
              <div>
                <IoRemove
                  className='icon'
                  style={
                    width === minWidth ? { cursor: 'auto', color: '#787878' } : { cursor: 'pointer', color: '#fff' }
                  }
                  onClick={handleZoomOut}
                />
                {width !== size.width && <BsZoomIn className='icon-zoom' onClick={() => setWidth(size.width)} />}
                {width === size.width && (
                  <BsZoomOut className='icon-zoom' onClick={() => setWidth((size.width * 2) / 3)} />
                )}
                <IoAdd
                  className='icon'
                  style={
                    width === maxWidth ? { cursor: 'auto', color: '#787878' } : { cursor: 'pointer', color: '#fff' }
                  }
                  onClick={handleZoomIn}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <ModalStyle
        title={false}
        open={isVisible}
        onCancel={() => {
          setIsVisible(false)
        }}
        footer={false}
        width={width * scale}
        closable={false}
      >
        {fileUrl && isImage && <img src={fileUrl} alt='img' style={{ width: '100%' }} />}
        {fileUrl && isPDF && (
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={() => (
              <div
                style={{
                  width: '100%',
                  height: size.height,
                  backgroundColor: '#fff',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Spin style={{ marginTop: 50 }} />
              </div>
            )}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <PageWithObserver
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                setPageVisibility={setPageVisibility}
                width={width}
                scale={scale}
              />
            ))}
          </Document>
        )}
      </ModalStyle>
    </PreviewStyle>
  )
}

function PageWithObserver({ pageNumber, setPageVisibility, scale, ...otherProps }) {
  const [page, setPage] = useState()
  const onIntersectionChange = useCallback(
    ([entry]) => {
      setPageVisibility(pageNumber, entry.isIntersecting)
    },
    [pageNumber, setPageVisibility]
  )

  useIntersectionObserver(page, observerConfig, onIntersectionChange)

  return (
    <>
      <Page canvasRef={setPage} pageNumber={pageNumber} {...otherProps} scale={scale} loading={false} />
      <div className='separator' />
    </>
  )
}

const ModalStyle = styled(Modal)`
  .ant-modal-wrap {
    .ant-modal {
      width: ${props => props.width}px;
    }
  }
  .ant-modal-header {
    width: ${props => props.width}px;
    background-color: #fff;
    padding: 16px 16px 0px 16px;
  }
  .ant-modal-content {
    background-color: transparent;
  }
  .ant-modal-body {
    width: ${props => props.width}px;
    background-color: rgba(0, 0, 0, 0.1);
    padding: 0px;
  }
  .button-container {
    display: flex;
    flex-direction: row;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);
    border-radius: 4px;
  }
  .separator {
    height: 20px;
    width: 100%;
  }
  .react-pdf__Page {
    background-color: #fff;
  }
`

const PreviewStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  .body {
    position: fixed;
    z-index: 1003;
    width: ${props => props.width}px;
    left: 0px;
  }
  .top {
    position: fixed;
    display: flex;
    align-items: center;
    top: 35px;
    left: 35px;
    cursor: pointer;
    p {
      color: #fff;
      font-size: 16px;
      margin: 0px;
    }
  }
  .bottom {
    position: fixed;
    display: flex;
    justify-content: center;
    bottom: 30px;
    width: ${props => props.width}px;
    .page-ctn {
      background-color: rgba(0, 0, 0, 0.93);
      padding: 10px 12px;
      display: flex;
      border-radius: 4px;
      align-items: center;

      p {
        font-size: 14px;
        color: #fff;
        margin: 0px;
      }
      .icon {
        font-size: 20px;
      }
      .icon-zoom {
        color: #fff;
        font-size: 17px;
        margin-right: 15px;
        margin-left: 15px;
        cursor: pointer;
      }
    }
  }
`